import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import {
  isAuthPortalPath,
  getClientPostAuthPath,
  isClientWebRole,
  CLIENT_PORTAL_DISABLED_MESSAGE,
} from '@core/portal/clientPortal.util';
import {
  buildPortalAccessContext,
  getDefaultHomePath,
  getPortalAlignmentRedirect,
} from '@/config/portalRoutes.config';
import {
  getCurrentUser,
  signOut,
  signIn,
  signUp,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
} from 'aws-amplify/auth';
import { useUserStore } from '@core/user/useUserStore';
import { usePermissionStore } from '@core/permission/usePermissionStore';
import { User, UserRole } from '@core/generated/gql-types';
import {
  ERROR_CODE_INCORRECT_APP_CONTEXT,
  findErrorByCode,
} from '@core/util/graphqlError.util';
import { useTenantId, useTenant } from './TenantContext';

const USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException';

const clearAppAuthState = () => {
  useUserStore.getState().clearUser();
  usePermissionStore.getState().clearPermissions();
  document.cookie =
    'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
};

const clearStaleCognitoSession = async () => {
  try {
    await getCurrentUser();
    await signOut();
  } catch {
    // No active Cognito session to clear.
  }
};

interface SignInParams {
  email: string; // User's email address (will be converted to tenantId#email for Cognito)
  password: string;
  redirectPath?: string;
}

interface SignUpParams {
  email: string; // User's email address
  password: string;
  confirmPassword: string;
}

interface ResetPasswordParams {
  email: string;
  confirmationCode: string;
  newPassword: string;
}

interface SignInResult {
  success: boolean;
  requiresConfirmation?: boolean;
  requiresNewPassword?: boolean;
  requiresPasswordReset?: boolean;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  checkAuth: (redirectPath?: string) => Promise<void>;
  signOut: () => Promise<void>;
  signIn: (params: SignInParams) => Promise<SignInResult>;
  confirmSignIn: (newPassword: string) => Promise<{ success: boolean }>;
  signUp: (
    params: SignUpParams,
  ) => Promise<{ success: boolean; email: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean }>;
  resetPassword: (params: ResetPasswordParams) => Promise<{ success: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { fetchCurrentUser } = useUserStore();
  const currentTenantId = useTenantId();
  const { enableClientPortal, isLoading: isTenantLoading } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const portalNavigate = usePortalNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isSigningOutRef = useRef(false);
  const userId = user?.id;

  // Backend permissions follow the signed-in user (including impersonation).
  useEffect(() => {
    if (userId) {
      void usePermissionStore.getState().fetchPermissions(userId);
    } else {
      usePermissionStore.getState().clearPermissions();
    }
  }, [userId]);

  const redirectUnauthenticatedUser = useCallback(() => {
    if (!isSigningOutRef.current && !isAuthPortalPath(location.pathname)) {
      portalNavigate('/login', { replace: true });
    }
  }, [location.pathname, portalNavigate]);

  const checkAuth = useCallback(
    async (redirectPath?: string) => {
      if (isSigningOutRef.current) {
        return;
      }

      try {
        setIsLoading(true);
        const cognitoUser = await getCurrentUser();

        const currentUser = await fetchCurrentUser();

        if (!currentUser) {
          await clearStaleCognitoSession();
          clearAppAuthState();
          setUser(null);
          redirectUnauthenticatedUser();
          return;
        }

        const userRole = currentUser.role;
        if (
          !isTenantLoading &&
          !enableClientPortal &&
          currentTenantId &&
          userRole != null &&
          isClientWebRole(userRole)
        ) {
          await signOut();
          clearAppAuthState();
          setUser(null);
          throw new Error(CLIENT_PORTAL_DISABLED_MESSAGE);
        }

        // Validate tenant access
        // HQ_ADMIN users can access any tenant
        if (currentUser && currentTenantId) {
          if (
            currentUser.tenantId !== currentTenantId &&
            currentUser.role !== UserRole.HqAdmin
          ) {
            // User doesn't belong to current tenant and is not HQ_ADMIN
            await signOut();
            setUser(null);
            // Clear cookie
            document.cookie =
              'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
            throw new Error('Your account is not part of this tenant');
          }
        }

        setUser(currentUser);

        // Set cookie for auth state
        document.cookie = `auth_token=true; path=/`;

        if (cognitoUser && currentUser?.role) {
          if (redirectPath) {
            navigate(redirectPath, { replace: true });
            return;
          }

          const alignmentRedirect = getPortalAlignmentRedirect(
            location.pathname,
            currentUser.role,
          );

          if (alignmentRedirect) {
            navigate(alignmentRedirect, { replace: true });
            return;
          }

          if (isAuthPortalPath(location.pathname)) {
            const access = buildPortalAccessContext(currentUser.role);
            const homePath = access.isClientPortal
              ? getClientPostAuthPath(currentUser)
              : getDefaultHomePath(access);

            navigate(homePath, { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        await clearStaleCognitoSession();
        clearAppAuthState();
        setUser(null);

        const shouldPropagateError =
          (error instanceof Error &&
            error.message === CLIENT_PORTAL_DISABLED_MESSAGE) ||
          findErrorByCode(error, ERROR_CODE_INCORRECT_APP_CONTEXT);

        if (shouldPropagateError) {
          setIsLoading(false);
          throw error;
        }

        redirectUnauthenticatedUser();
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentTenantId,
      enableClientPortal,
      isTenantLoading,
      fetchCurrentUser,
      location.pathname,
      navigate,
      redirectUnauthenticatedUser,
    ],
  );

  const handleSignOut = async () => {
    try {
      isSigningOutRef.current = true;
      setUser(null);
      clearAppAuthState();
      await signOut();
      Object.keys(localStorage).forEach((key) => {
        if (key.endsWith('-storage')) {
          localStorage.removeItem(key);
        }
      });
      portalNavigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      portalNavigate('/login', { replace: true });
    } finally {
      isSigningOutRef.current = false;
    }
  };

  const handleSignIn = useCallback(
    async (params: SignInParams): Promise<SignInResult> => {
      const { password } = params;
      const email = params.email.trim().toLowerCase();

      // Try tenantId#email format first if tenantId is available, then fallback to just email
      let cognitoUsername = currentTenantId
        ? `${currentTenantId}#${email}`
        : email;

      const attemptSignIn = async (username: string) => {
        return signIn({
          username,
          password,
          options: {
            authFlowType: 'USER_PASSWORD_AUTH',
          },
        });
      };

      try {
        let signInOutput;
        try {
          signInOutput = await attemptSignIn(cognitoUsername);
        } catch (firstAttemptError) {
          // Check for PasswordResetRequiredException
          if (
            firstAttemptError instanceof Error &&
            firstAttemptError.name === 'PasswordResetRequiredException'
          ) {
            return {
              success: false,
              requiresPasswordReset: true,
              email,
            };
          }

          // Don't retry with different username format if it was wrong password
          if (
            firstAttemptError instanceof Error &&
            firstAttemptError.name === 'NotAuthorizedException'
          ) {
            throw firstAttemptError;
          }

          // If tenantId#email format fails and we have a tenantId, try just email as fallback
          if (currentTenantId && cognitoUsername.includes('#')) {
            cognitoUsername = email;
            try {
              signInOutput = await attemptSignIn(cognitoUsername);
            } catch (secondAttemptError) {
              // Check for PasswordResetRequiredException on fallback attempt
              if (
                secondAttemptError instanceof Error &&
                secondAttemptError.name === 'PasswordResetRequiredException'
              ) {
                return {
                  success: false,
                  requiresPasswordReset: true,
                  email,
                };
              }
              throw secondAttemptError;
            }
          } else {
            throw firstAttemptError;
          }
        }

        if (signInOutput.isSignedIn) {
          // Fetch current user after successful sign in
          const redirectPath = params.redirectPath || '/';
          await checkAuth(redirectPath);
          return { success: true };
        } else if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          return {
            success: false,
            requiresConfirmation: true,
            email,
          };
        } else if (
          signInOutput.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          return {
            success: false,
            requiresNewPassword: true,
            email,
          };
        }
        return { success: false };
      } catch (error) {
        if (
          error instanceof Error &&
          error.name === USER_ALREADY_AUTHENTICATED
        ) {
          const redirectPath = params.redirectPath || '/';
          await checkAuth(redirectPath);
          return { success: true };
        }

        await clearStaleCognitoSession();
        clearAppAuthState();
        throw error;
      }
    },
    [checkAuth, currentTenantId],
  );

  const handleSignUp = useCallback(
    async (params: SignUpParams) => {
      const email = params.email.trim().toLowerCase();

      // Validation
      if (!email) {
        throw new Error('Email is required');
      }

      if (params.password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      if (params.password !== params.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (!currentTenantId) {
        throw new Error('Tenant ID is required for sign up');
      }

      // Construct Cognito username as tenantId#email
      const cognitoUsername = `${currentTenantId}#${email}`;

      const signUpOutput = await signUp({
        username: cognitoUsername,
        password: params.password,
        options: {
          userAttributes: {
            email,
          },
        },
      });

      if (signUpOutput.nextStep) {
        return {
          success: true,
          email,
        };
      }
      return { success: false, email };
    },
    [currentTenantId],
  );

  const handleForgotPassword = useCallback(
    async (emailParam: string) => {
      const email = emailParam.trim().toLowerCase();

      // Try tenantId#email format first if tenantId is available, then fallback to just email
      let cognitoUsername = currentTenantId
        ? `${currentTenantId}#${email}`
        : email;

      try {
        await resetPassword({ username: cognitoUsername });
        return { success: true };
      } catch (firstAttemptError) {
        // If tenantId#email format fails and we have a tenantId, try just email as fallback
        if (currentTenantId && cognitoUsername.includes('#')) {
          cognitoUsername = email;
          await resetPassword({ username: cognitoUsername });
          return { success: true };
        } else {
          throw firstAttemptError;
        }
      }
    },
    [currentTenantId],
  );

  const handleResetPassword = useCallback(
    async (params: ResetPasswordParams) => {
      const email = params.email.trim().toLowerCase();

      // Try tenantId#email format first if tenantId is available, then fallback to just email
      let cognitoUsername = currentTenantId
        ? `${currentTenantId}#${email}`
        : email;

      try {
        await confirmResetPassword({
          username: cognitoUsername,
          confirmationCode: params.confirmationCode,
          newPassword: params.newPassword,
        });
        return { success: true };
      } catch (firstAttemptError) {
        // If tenantId#email format fails and we have a tenantId, try just email as fallback
        if (currentTenantId && cognitoUsername.includes('#')) {
          cognitoUsername = email;
          await confirmResetPassword({
            username: cognitoUsername,
            confirmationCode: params.confirmationCode,
            newPassword: params.newPassword,
          });
          return { success: true };
        } else {
          throw firstAttemptError;
        }
      }
    },
    [currentTenantId],
  );

  const handleConfirmSignIn = useCallback(
    async (newPassword: string) => {
      try {
        const confirmSignInOutput = await confirmSignIn({
          challengeResponse: newPassword,
        });

        if (confirmSignInOutput.isSignedIn) {
          await checkAuth('/');
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        console.error('Error confirming sign in:', error);
        throw error;
      }
    },
    [checkAuth],
  );

  useEffect(() => {
    void checkAuth().catch(() => {
      // Login and sign-in flows handle propagated auth errors explicitly.
    });
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        checkAuth,
        signOut: handleSignOut,
        signIn: handleSignIn,
        confirmSignIn: handleConfirmSignIn,
        signUp: handleSignUp,
        forgotPassword: handleForgotPassword,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
